import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { operations, type ProposalSettings } from '@/shared/api/resources';
import {
  formatDateTime,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/** Etapas que podem marcar o inicio da proposta na linha do tempo (P-COT-3). */
const etapasDaProposta = [
  { value: 'em_subscricao', label: 'Em subscricao' },
  { value: 'em_juridico', label: 'Em juridico' },
  { value: 'aguardando_comercial', label: 'Aguardando comercial (padrao)' },
  { value: 'minuta_gerada', label: 'Minuta gerada' },
  { value: 'aguardando_ccg', label: 'Aguardando CCG' },
  { value: 'aguardando_assinatura', label: 'Aguardando assinatura' },
  { value: 'em_emissao', label: 'Em emissao' },
];

/**
 * Confere a comissao padrao digitada: um percentual de 0 a 100, com ate duas casas, como
 * a comissao da operacao. O servidor confere de novo ao gravar.
 */
function erroDaComissaoPadrao(texto: string): string | undefined {
  if (texto.trim() === '') return 'Informe a comissao padrao.';
  const invalida = rateInputError(texto);
  if (invalida) return invalida;
  const pontos = rateToBasisPoints(texto);
  if (pontos < 0 || pontos > 10000) {
    return 'A comissao padrao precisa estar entre 0 e 100 por cento.';
  }
  return undefined;
}

/**
 * Configuracao da cotacao da empresa (decisoes abertas do dono, ADR 0036): quem gera
 * o numero de 16 digitos, qual situacao do OnPoint o declinio representa e a comissao
 * padrao (decisao do dono de 03/11/2026, OPEN_QUESTIONS 2.97). O padrao segue o OnPoint
 * e, na comissao, 20%; a troca vale daqui em diante e fica na trilha.
 */
export function QuoteSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: ['operations', 'proposal-settings'],
    queryFn: ({ signal }) => operations.proposalSettings(signal),
    enabled: open,
  });
  const [rascunho, setRascunho] = useState<ProposalSettings | null>(null);
  const atual = rascunho ?? consulta.data ?? null;
  // A comissao e digitada como percentual e gravada em pontos-base. Sem digitar, o campo
  // mostra a gravada e o pedido leva a gravada.
  const [comissaoDigitada, setComissaoDigitada] = useState<string | null>(null);
  const comissaoGravada = atual?.default_commission_bps;
  const comissao =
    comissaoDigitada ??
    (comissaoGravada !== undefined ? rateToInput(comissaoGravada) : '');
  const erroDaComissao =
    comissaoDigitada === null ? undefined : erroDaComissaoPadrao(comissaoDigitada);

  const gravar = useMutation({
    mutationFn: (input: ProposalSettings) => operations.updateProposalSettings(input),
    onSuccess: () => {
      toast.success('Configuracao da cotacao gravada.');
      setRascunho(null);
      setComissaoDigitada(null);
      void queryClient.invalidateQueries({
        queryKey: ['operations', 'proposal-settings'],
      });
      // As opcoes do assistente e da Nova operacao trazem a comissao padrao.
      void queryClient.invalidateQueries({ queryKey: ['broker-quote-options'] });
      onClose();
    },
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  function fechar() {
    setRascunho(null);
    setComissaoDigitada(null);
    gravar.reset();
    onClose();
  }

  function salvar() {
    if (!atual || erroDaComissao) return;
    gravar.mutate(
      comissaoDigitada === null
        ? atual
        : { ...atual, default_commission_bps: rateToBasisPoints(comissaoDigitada) },
    );
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Configuracao da cotacao"
      description="Vale para as cotacoes abertas e as transicoes daqui em diante."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={!atual || erroDaComissao !== undefined}
            onClick={salvar}
          >
            Salvar configuracao
          </Button>
        </>
      }
    >
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {consulta.error.message}
        </Alert>
      ) : !atual ? (
        <SkeletonText lines={3} />
      ) : (
        <div className="flex flex-col gap-4">
          {gravar.error ? (
            <Alert tone="danger" title="Nao foi possivel salvar">
              {gravar.error.message}
            </Alert>
          ) : null}
          <Select
            label="Geracao do numero da cotacao"
            value={atual.number_mode}
            options={[
              { value: 'automatico', label: 'Gerado pelo sistema na abertura' },
              { value: 'informado', label: 'Informado (sistema da seguradora)' },
            ]}
            error={fieldErrorOf(apiError, 'number_mode')}
            onChange={(event) =>
              setRascunho({
                ...atual,
                number_mode: event.target.value as ProposalSettings['number_mode'],
              })
            }
          />
          <Field
            label="Prefixo do numero"
            inputMode="numeric"
            maxLength={6}
            value={atual.number_prefix}
            disabled={atual.number_mode !== 'automatico'}
            error={fieldErrorOf(apiError, 'number_prefix')}
            onChange={(event) =>
              setRascunho({
                ...atual,
                number_prefix: event.target.value.replace(/\D/g, ''),
              })
            }
          />
          <Select
            label="Situacao do declinio"
            value={atual.declined_status}
            options={[
              { value: 'recusada', label: 'Recusada' },
              { value: 'rejeitada', label: 'Rejeitada' },
              { value: 'cancelada', label: 'Cancelada' },
            ]}
            error={fieldErrorOf(apiError, 'declined_status')}
            onChange={(event) =>
              setRascunho({
                ...atual,
                declined_status: event.target
                  .value as ProposalSettings['declined_status'],
              })
            }
          />
          {/* Fase da linha do tempo (P-COT-3): o OnPoint nao diz quando a cotacao vira
              proposta, e a empresa escolhe. */}
          <Select
            label="Etapa em que vira proposta"
            value={atual.timeline_proposal_stage ?? 'aguardando_comercial'}
            options={etapasDaProposta}
            error={fieldErrorOf(apiError, 'timeline_proposal_stage')}
            onChange={(event) =>
              setRascunho({ ...atual, timeline_proposal_stage: event.target.value })
            }
          />
          {/* Decisao do dono de 03/11/2026 (OPEN_QUESTIONS 2.97): o campo Comissao da
              operacao nova vem com este valor quando a corretora nao tem percentual
              cadastrado, e continua editavel. */}
          <Field
            label="Comissao padrao (%)"
            inputMode="decimal"
            value={comissao}
            error={erroDaComissao ?? fieldErrorOf(apiError, 'default_commission_bps')}
            hint="Preenche o campo Comissao da operacao nova quando a corretora nao tem percentual cadastrado, sem passar do limite de comissao dela. O campo continua editavel, e as operacoes ja abertas nao mudam."
            onChange={(event) => setComissaoDigitada(event.target.value)}
          />
          {/* Decisoes do dono de 03/11/2026 (OPEN_QUESTIONS 2.97 e 2.98). */}
          <Select
            label="Comissao padrao vale para"
            value={atual.default_commission_scope ?? 'geral'}
            options={[
              { value: 'geral', label: 'Todos os produtos e modalidades (geral)' },
            ]}
            error={fieldErrorOf(apiError, 'default_commission_scope')}
            hint="Por ora a comissao padrao e geral. O padrao por produto ou modalidade fica para quando for preciso."
            // So ha a abrangencia geral por ora: a escolha grava sempre "geral".
            onChange={() =>
              setRascunho({ ...atual, default_commission_scope: 'geral' })
            }
          />
          <OpcaoDaConfiguracao
            label="Aceitar comissao acima do limite da corretora"
            checked={atual.commission_above_broker_cap === true}
            hint="Desligada, a comissao acima do limite de comissao cadastrado na corretora e recusada na operacao e na cotacao da corretora."
            onChange={(ligada) =>
              setRascunho({ ...atual, commission_above_broker_cap: ligada })
            }
          />
          <OpcaoDaConfiguracao
            label="Usar a comissao do tomador na sugestao"
            checked={atual.use_policyholder_commission === true}
            hint="Ligada, a comissao cadastrada no tomador vem antes da comissao da corretora e da padrao. O campo continua editavel."
            onChange={(ligada) =>
              setRascunho({ ...atual, use_policyholder_commission: ligada })
            }
          />
          <OpcaoDaConfiguracao
            label="Aprovar o credito da operacao pelo credito do tomador"
            checked={atual.credit_auto_from_policyholder !== false}
            hint="Ligada, a analise de credito da operacao aprova sozinha quando o tomador tem limite aprovado e vigente no grupo da modalidade, sem analise aberta ou recusada, e o valor garantido cabe no disponivel. Senao, fica com uma pessoa."
            onChange={(ligada) =>
              setRascunho({ ...atual, credit_auto_from_policyholder: ligada })
            }
          />
          {atual.updated_by_label ? (
            <p className="text-2xs text-ink-500">
              Alterada por {atual.updated_by_label}
              {atual.updated_at ? ` em ${formatDateTime(atual.updated_at)}` : ''}.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/** Uma opcao liga e desliga da configuracao, com o texto do que ela faz. */
function OpcaoDaConfiguracao({
  label,
  checked,
  hint,
  onChange,
}: {
  label: string;
  checked: boolean;
  hint: string;
  onChange: (ligada: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-line px-3 py-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-ink-800">{label}</span>
        <span className="text-2xs text-ink-500">{hint}</span>
      </span>
    </label>
  );
}
